import { Sites } from '@ps/platform-message-framework/common/Sites';
import { ResolverTag } from '@ps/web-core-enums';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import {
  aboutRgCashierLogo,
  adm,
  admStar,
  alwaysPlayLegally,
  anj,
  autoProhibicion,
  container,
  dge,
  evalujeu,
  fejar,
  footer,
  fpsDe,
  fpsEn,
  fpsNl,
  gambleAware,
  gambler,
  gamblingCommission,
  gameCareUk,
  gamingControlBoard,
  gamingLabs,
  gamstop,
  gordonMoody,
  grHgc,
  grKethea,
  grandCasino,
  ibas,
  ibia,
  igamingOntario,
  juegoSeguro,
  jugarBien,
  logico,
  logo,
  logos,
  mga,
  minus18Color,
  onjn,
  plus18,
  plus19,
  plus21,
  resGamingCasino,
  resorts,
  responsibleGaming,
  rg,
  sal,
  sicad,
  srij,
  stopSpillet,
  takeTimeToThink
} from './Footer.scss';

enum Locale {
  NL = 'nl',
  DE = 'de'
}

const getFpsLogo = (locale: string) => {
  const logo: Record<string, string> = {
    [Locale.NL]: fpsNl,
    [Locale.DE]: fpsDe
  };
  return logo[locale] || fpsEn;
};

const getLogos = (siteId: Sites, locale: string) => {
  const map: Partial<{ [key in Sites]: string[] }> = {
    [Sites.COM]: [gordonMoody, plus18, mga, ibia, rg],
    [Sites.ITA]: [admStar, adm, gamingLabs, plus18, logico, ibia],
    [Sites.FRA]: [minus18Color, anj, gamingLabs, evalujeu, ibia],
    [Sites.EST]: [plus21],
    [Sites.BEL]: [getFpsLogo(locale), grandCasino, alwaysPlayLegally, plus21],
    [Sites.DE]: [plus18],
    [Sites.SPA]: [plus18, juegoSeguro, jugarBien, autoProhibicion, fejar, ibia],
    [Sites.DEN]: [gamingLabs, gordonMoody, stopSpillet, plus18, ibia],
    [Sites.EU]: [gordonMoody, plus18, mga, ibia, rg],
    [Sites.UK]: [
      plus18,
      gamblingCommission,
      gamingLabs,
      aboutRgCashierLogo,
      gamstop,
      gordonMoody,
      gameCareUk,
      gambleAware,
      takeTimeToThink,
      ibas,
      ibia
    ],
    [Sites.BUL]: [plus18],
    [Sites.RU]: [mga, plus18],
    [Sites.GR]: [gamingLabs, plus21, grKethea, grHgc],
    [Sites.POR]: [srij, gamingLabs, gordonMoody, sicad, plus18],
    [Sites.US_NJ]: [dge, plus21, gambler, resorts, responsibleGaming],
    [Sites.USMI]: [plus21, responsibleGaming],
    [Sites.US_PA]: [plus21, gambler, gamingControlBoard, resGamingCasino],
    [Sites.ROM]: [plus18, onjn, gamingLabs, gordonMoody, ibia, sal],
    [Sites.CA_ON]: [igamingOntario, plus19, rg, gordonMoody, gamingLabs]
  };
  return map[siteId];
};

interface Props {
  showFooter?: boolean;
  modal?: boolean;
}

const Footer: React.FC<Props> = ({ showFooter, modal }) => {
  const siteId = useSelector(select.cms.getSiteId);
  const locale = useSelector(select.locale.get);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const hiddenLabsLogosSites = [
    Sites.US_NJ,
    Sites.USMI,
    Sites.US_PA,
    Sites.DE,
    Sites.DEN,
    Sites.ITA,
    Sites.UK,
    Sites.CA_ON,
    Sites.POR,
    Sites.FRA,
    Sites.ROM,
    Sites.GR
  ];

  const {
    data: { cmsHeaderAndGridLayout }
  } = useWizardContext();

  const getResolverTag = (logo: string) => {
    if (logo === plus18) {
      return ResolverTag.AboutRgCashierLogo;
    }
    const linksList: Partial<Record<Sites, { [key in string]: ResolverTag }>> = {
      [Sites.BEL]: {
        [plus21]: ResolverTag.ResponsibleGamingLogoBe
      },
      [Sites.DE]: {
        [plus18]: ResolverTag.HelpOver18
      },
      [Sites.DEN]: {
        [gamingLabs]: ResolverTag.GamingLabsLogo,
        [gordonMoody]: ResolverTag.GordonMoodyLogo,
        [stopSpillet]: ResolverTag.DKRegulatorLogo,
        [plus18]: ResolverTag.HelpOver18,
        [ibia]: ResolverTag.IbiaLogo
      },
      [Sites.COM]: {
        [rg]: ResolverTag.HelpRGAccredation,
        [ibia]: ResolverTag.IbiaLogo,
        [gordonMoody]: ResolverTag.GordonMoodyLogo,
        [mga]: ResolverTag.HelpMGA,
        [gamingLabs]: ResolverTag.GamingLabsLogo
      },
      [Sites.EU]: {
        [rg]: ResolverTag.HelpRGAccredation,
        [gordonMoody]: ResolverTag.GordonMoodyLogo,
        [mga]: ResolverTag.HelpMGA,
        [ibia]: ResolverTag.IbiaLogo,
        [gamingLabs]: ResolverTag.GamingLabsLogo
      },
      [Sites.UK]: {
        [gambleAware]: ResolverTag.BeGambleAwareLogo,
        [plus18]: ResolverTag.HelpOver18,
        [gamingLabs]: ResolverTag.GamingLabsLogo,
        [gamblingCommission]: ResolverTag.UKLicenseAccountNumber,
        [gameCareUk]: ResolverTag.GamCareHelplineLogo,
        [aboutRgCashierLogo]: ResolverTag.AboutRgCashierLogo,
        [gamstop]: ResolverTag.UKGamstopMain,
        [ibas]: ResolverTag.IbasLogo,
        [takeTimeToThink]: ResolverTag.TakeTimeToThink,
        [ibia]: ResolverTag.IbiaLogo,
        [gordonMoody]: ResolverTag.GordonMoodyLogo
      },
      [Sites.ROM]: {
        [plus18]: ResolverTag.AboutRgCashierLogo,
        [onjn]: ResolverTag.ONJNLogo,
        [gamingLabs]: ResolverTag.GamingLabsLogo,
        [gordonMoody]: ResolverTag.GordonMoodyLogo,
        [ibia]: ResolverTag.IbiaLogo,
        [sal]: ResolverTag.SalLgo
      },
      [Sites.US_NJ]: {
        [dge]: ResolverTag.NJDgeLogo,
        [plus21]: ResolverTag.HelpResponsibleGaming,
        [gambler]: ResolverTag.GamblerLogo800,
        [resorts]: ResolverTag.ResortsLogo,
        [responsibleGaming]: ResolverTag.HelpResponsibleGaming
      },
      [Sites.USMI]: {
        [plus21]: ResolverTag.HelpResponsibleGaming,
        [responsibleGaming]: ResolverTag.HelpResponsibleGaming
      },
      [Sites.US_PA]: {
        [plus21]: ResolverTag.HelpResponsibleGaming,
        [gambler]: ResolverTag.PACouncilLogo,
        [gamingControlBoard]: ResolverTag.GamingControlBoard,
        [resGamingCasino]: ResolverTag.HelpResponsibleGaming
      },
      [Sites.ITA]: {
        [admStar]: ResolverTag.ADM,
        [adm]: ResolverTag.ADMResponsibleGaming,
        [gamingLabs]: ResolverTag.GamingLabsLogo,
        [plus18]: ResolverTag.AboutRgCashierLogo,
        [logico]: ResolverTag.Logico,
        [ibia]: ResolverTag.IbiaLogo
      },
      [Sites.CA_ON]: {
        [igamingOntario]: ResolverTag.IgoContact,
        [plus19]: ResolverTag.AboutRgCashierLogo,
        [rg]: ResolverTag.HelpRGAccredation,
        [gordonMoody]: ResolverTag.GordonMoodyLogo,
        [gamingLabs]: ResolverTag.GamingLabsLogo
      },
      [Sites.POR]: {
        [srij]: ResolverTag.SrijPT,
        [gamingLabs]: ResolverTag.GamingLabsLogo,
        [gordonMoody]: ResolverTag.GordonMoodyLogo,
        [sicad]: ResolverTag.SicadPT,
        [plus18]: ResolverTag.AboutRgCashierLogo
      },
      [Sites.SPA]: {
        [plus18]: ResolverTag.AboutRgCashierLogo,
        [juegoSeguro]: ResolverTag.JuegoSeguroLogo,
        [jugarBien]: ResolverTag.JugarBien,
        [autoProhibicion]: ResolverTag.AutoProhibicionLogo,
        [fejar]: ResolverTag.FejarLogo,
        [ibia]: ResolverTag.IbiaLogo
      },
      [Sites.FRA]: {
        [minus18Color]: ResolverTag.HelpOver18,
        [anj]: ResolverTag.ANJLogo,
        [gamingLabs]: ResolverTag.GamingLabsLogo,
        [evalujeu]: ResolverTag.EvalujeuLogo,
        [ibia]: ResolverTag.IbiaLogo
      },
      [Sites.GR]: {
        [gamingLabs]: ResolverTag.GamingLabsLogo,
        [plus21]: ResolverTag.HelpOver18,
        [grKethea]: ResolverTag.GrKethea,
        [grHgc]: ResolverTag.GrHgc
      }
    };
    return linksList?.[siteId]?.[logo];
  };

  const wizardFooter = (
    <footer className={footer}>
      <div className={container}>
        <div className={logos}>
          {!hiddenLabsLogosSites.includes(siteId) && (
            <ResolverLink resolverTag={ResolverTag.GamingLabsLogo} key={gamingLabs}>
              <div className={classNames(logo, gamingLabs)} data-testid={gamingLabs} />
            </ResolverLink>
          )}
          {getLogos(siteId, locale)?.map((className, key) => {
            const resolverTag = getResolverTag(className);
            const item = (
              <div key={key} data-testid={className} className={classNames(logo, className)} />
            );
            return resolverTag ? (
              <ResolverLink resolverTag={resolverTag} key={key}>
                {item}
              </ResolverLink>
            ) : (
              item
            );
          })}
        </div>
      </div>
    </footer>
  );

  const getFooterType = () => {
    if (cmsHeaderAndGridLayout) {
      if (isEmbedded) {
        return wizardFooter;
      }
      return null;
    } else {
      if (showFooter) {
        return wizardFooter;
      }
      return null;
    }
  };

  return !modal ? getFooterType() : null;
};

export default Footer;
